import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Edit,
  Person,
  Email,
  Phone,
  FitnessCenter,
  Timeline,
  Add,
  Close,
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { format } from 'date-fns';

const ClientManagement = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    activePrograms: 0,
    newClientsThisMonth: 0,
    averageSessionsPerClient: 0
  });
  const [bookingsDialogOpen, setBookingsDialogOpen] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsResponse, statsResponse] = await Promise.all([
        axios.get('/api/v2/admin/clients'),
        axios.get('/api/v2/admin/stats')
      ]);

      console.log('Clients response:', clientsResponse.data);

      const clientsData = clientsResponse.data.clients;
      setClients(clientsData);

      setStats({
        totalClients: statsResponse.data.activeClients,
        activePrograms: clientsData.filter(c => c.upcomingSessions > 0).length,
        newClientsThisMonth: clientsData.length,
        averageSessionsPerClient: clientsData.reduce((sum, client) => 
          sum + (client.totalSessions || 0), 0) / clientsData.length || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (event, client) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedClientData(client);
    setDialogOpen(true);
  };

  const handleCloseDialog = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setDialogOpen(false);
    setTimeout(() => setSelectedClientData(null), 200);
  };

  const handleSaveClient = async (clientData) => {
    try {
      if (selectedClientData) {
        // Update existing client
        await axios.put(`/api/v2/admin/clients/${selectedClientData._id}`, clientData);
      } else {
        // Create new client
        await axios.post('/api/v2/admin/clients', clientData);
      }
      await fetchData(); // Refresh the client list
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await axios.delete(`/api/v2/admin/clients/${clientId}`);
      await fetchData(); // Refresh the client list
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    try {
      await axios.put(`/api/v2/admin/bookings/${bookingId}/confirm`);
      // Refresh the client data to update the UI
      await fetchData();
      // Close the dialog
      setBookingsDialogOpen(false);
    } catch (error) {
      console.error('Error confirming payment:', error);
    }
  };

  const handleBookingsClick = (client) => {
    console.log('Client:', client);
    setSelectedBookings(client.bookings || []);
    setBookingsDialogOpen(true);
  };

  const handleConfirmBooking = async (bookingId) => {
    try {
      await axios.put(`/api/v2/admin/bookings/${bookingId}/confirm`);
      // Refresh the client list to update the status
      await fetchData();
      // Close the dialog
      setBookingsDialogOpen(false);
    } catch (error) {
      console.error('Error confirming booking:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Client Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={(e) => {
            e.preventDefault();
            setDialogOpen(true);
          }}
        >
          Add New Client
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h4">{stats.totalClients}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Programs
                  </Typography>
                  <Typography variant="h4">{stats.activePrograms}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    New Clients This Month
                  </Typography>
                  <Typography variant="h4">{stats.newClientsThisMonth}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Sessions/Client
                  </Typography>
                  <Typography variant="h4">
                    {stats.averageSessionsPerClient.toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Parent's Name</TableCell>
                  <TableCell>Player's Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Total Sessions</TableCell>
                  <TableCell>Upcoming</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => {
                    const hasPendingBooking = client.bookings?.some(b => b.status === 'pending');
                    return (
                      <TableRow 
                        key={client._id}
                        sx={{
                          backgroundColor: hasPendingBooking ? '#ffebee' : 'inherit'
                        }}
                      >
                        <TableCell>{client.parentName}</TableCell>
                        <TableCell>{client.playerName}</TableCell>
                        <TableCell>{client.email}</TableCell>
                        <TableCell>{client.phone}</TableCell>
                        <TableCell>
                          {format(new Date(client.joinDate), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{client.totalSessions}</TableCell>
                        <TableCell>
                          {client.upcomingSessions > 0 && (
                            <Chip
                              label={`${client.upcomingSessions} upcoming`}
                              color="primary"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box 
                            component="div" 
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'flex', gap: 1 }}
                          >
                            <Button
                              startIcon={<Edit />}
                              onClick={(e) => handleEditClick(e, client)}
                              size="small"
                              color="primary"
                              sx={{ textTransform: 'none' }}
                            >
                              Edit
                            </Button>
                            {hasPendingBooking && (
                              <Button
                                onClick={() => handleConfirmPayment(client.bookings.find(b => b.status === 'pending')._id)}
                                size="small"
                                color="success"
                                variant="contained"
                                sx={{ textTransform: 'none' }}
                              >
                                Confirm Payment
                              </Button>
                            )}
                            <Button
                              onClick={() => handleBookingsClick(client)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ textTransform: 'none' }}
                            >
                              Bookings
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={clients.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedClientData ? 'Edit Client Details' : 'Add New Client'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Parent's Name</Typography>
              </Box>
              <Typography variant="h6" color="primary">
                {selectedClientData?.parentName || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FitnessCenter sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Player's Name</Typography>
              </Box>
              <Typography variant="h6" color="primary">
                {selectedClientData?.playerName || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Email</Typography>
              </Box>
              <Typography variant="h6">
                {selectedClientData?.email || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Phone</Typography>
              </Box>
              <Typography variant="h6">
                {selectedClientData?.phone || 'Not specified'}
              </Typography>
            </Grid>
            {selectedClientData && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Timeline sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Session History</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${selectedClientData.totalSessions || 0} Total Sessions`}
                      color="primary"
                    />
                    {selectedClientData.upcomingSessions > 0 && (
                      <Chip
                        label={`${selectedClientData.upcomingSessions} Upcoming`}
                        color="secondary"
                      />
                    )}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bookings Dialog */}
      <Dialog 
        open={bookingsDialogOpen} 
        onClose={() => setBookingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Client Bookings
          <IconButton
            aria-label="close"
            onClick={() => setBookingsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedBookings?.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell>{format(new Date(booking.date), 'MMM d, yyyy')}</TableCell>
                  <TableCell>{booking.session}</TableCell>
                  <TableCell>
                    <Chip 
                      label={booking.status}
                      color={booking.status === 'pending' ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>${booking.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    {booking.status === 'pending' && (
                      <Button
                        onClick={() => handleConfirmPayment(booking._id)}
                        size="small"
                        color="success"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                      >
                        Confirm Payment
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClientManagement;
